<template>
    <div width="800px">
        <v-col v-if="drawer" class="pa-0">
            <v-sheet elevation="0">
                <v-data-table
                    :headers="headers"
                    :items="packingListsFiltered"
                    :loading="delayedPackingListsLoading || loading"
                    dense
                    fixed-header
                    height="100vh"
                    :footer-props="{
                        itemsPerPageOptions: [15, 30, 100, -1],
                        itemsPerPageText: 'Packing Lists Per Page',
                    }"
                    :custom-sort="customSort"
                >
                    <template v-slot:top>
                        <v-row
                            no-gutter
                            class="mt-0 mx-0 pt-4 pb-0 pb-4 align-center"
                            style="background-color: #eeeeee"
                        >
                            <v-col cols="12" class="d-flex align-center">
                                <v-btn
                                    icon
                                    small
                                    @click="drawer = false"
                                    class="mr-3"
                                >
                                    <v-icon>mdi-chevron-left</v-icon>
                                </v-btn>
                                <h3 class="text-h6 ma-0">
                                    Delayed Packing Lists
                                </h3>
                            </v-col>
                        </v-row>
                    </template>
                    <!--HEADERS-->
                    <template v-slot:[`header.deliveryDate`]="{ header }">
                        <v-text-field
                            class="py-2 mt-2"
                            :label="header.text"
                            v-model="deliveryDateFilter"
                            dense
                        />
                    </template>
                    <template v-slot:[`header.code`]="{ header }">
                        <v-text-field
                            class="py-2 mt-2"
                            :label="header.text"
                            v-model="packingListFilter"
                            dense
                        />
                    </template>
                    <template v-slot:[`header.status`]="{ header }">
                        <v-text-field
                            class="py-2 mt-2"
                            :label="header.text"
                            v-model="statusFilter"
                            dense
                        />
                    </template>
                    <template v-slot:[`item.status`]="{ item }">
                        {{ getPackingListStatusText(item) }}
                    </template>
                    <template v-slot:[`header.workOrders`]="{ header }">
                        <v-text-field
                            class="py-2 mt-2"
                            :label="header.text"
                            v-model="workOrdersFilter"
                            dense
                        />
                    </template>
                    <template v-slot:[`header.clasifications`]="{ header }">
                        <v-text-field
                            class="py-2 mt-2"
                            :label="header.text"
                            v-model="clasificationFilter"
                            dense
                        />
                    </template>
                    <template v-slot:[`header.notes`]="{ header }">
                        <v-text-field
                            class="py-2 mt-2"
                            :label="header.text"
                            v-model="descriptionFilter"
                            dense
                        />
                    </template>
                    <!--ITEMS-->
                    <template v-slot:[`item.deliveryDate`]="{ item }">
                        {{ getDeliveryDate(item) || '-' }}
                    </template>

                    <template v-slot:[`item.workOrders`]="{ item }">
                        {{
                            item.selectedWorkOrders &&
                            item.selectedWorkOrders.length > 0
                                ? item.selectedWorkOrders
                                      .map(wo => wo?.code)
                                      .join(', ')
                                : '-'
                        }}
                    </template>

                    <template v-slot:[`item.clasifications`]="{ item }">
                        {{
                            item.clasifications &&
                            item.clasifications.length > 0
                                ? item.clasifications?.join(', ')
                                : '-'
                        }}
                    </template>
                    <template v-slot:[`item.notes`]="{ item }">
                        {{ item.notes || '-' }}
                    </template>

                    <template v-slot:[`item.actions`]="{ item }">
                        <v-menu rounded offset-y>
                            <template v-slot:activator="{ attrs, on }">
                                <v-btn v-bind="attrs" v-on="on" icon x-small>
                                    <v-icon>mdi-dots-vertical</v-icon>
                                </v-btn>
                            </template>
                            <v-list dense>
                                <v-list-item @click="openViewPackingList(item)">
                                    <v-list-item-icon class="mr-2">
                                        <v-icon size="20"
                                            >mdi-package-variant</v-icon
                                        >
                                    </v-list-item-icon>
                                    <v-list-item-content>
                                        <v-list-item-title>
                                            View packing list
                                        </v-list-item-title>
                                    </v-list-item-content>
                                </v-list-item>
                                <v-list-item
                                    v-if="userPlaneationPermissions"
                                    @click="openUpdateScheduledFor(item)"
                                >
                                    <v-list-item-icon class="mr-2">
                                        <v-icon size="20"
                                            >mdi-calendar-edit</v-icon
                                        >
                                    </v-list-item-icon>
                                    <v-list-item-content>
                                        <v-list-item-title>
                                            Update scheduled for
                                        </v-list-item-title>
                                    </v-list-item-content>
                                </v-list-item>
                            </v-list>
                        </v-menu>
                    </template>
                </v-data-table>
            </v-sheet>
            <EditScheduledForDialog
                :packingList="selectedPackingListForEdit"
                :dialog="editScheduledForDialog"
                :projectId="selectedPackingListForEdit?.projectId || ''"
                @close="editScheduledForDialog = false"
                @updated="handleScheduledForUpdated"
            />
        </v-col>
    </div>
</template>

<script>
import { mapMutations } from 'vuex'
import _ from 'lodash'
import moment from 'moment'

export default {
    name: 'DelayedPackingLists',
    components: {
        EditScheduledForDialog: () =>
            import('@/components/ShipmentPlanner/EditScheduledForDialog.vue'),
    },
    props: {
        user: {
            type: Object,
            required: true,
        },
        value: {
            type: Boolean,
            default: false,
        },
        delayedPackingListsLoading: {
            type: Boolean,
            default: false,
        },
        delayedPackingLists: {
            type: Array,
            default: () => [],
        },
        woClasificationsData: {
            type: Object,
            default: () => ({}),
        },
    },
    computed: {
        drawer: {
            get() {
                return this.value
            },
            set(val) {
                this.$emit('input', val)
            },
        },
        userPlaneationPermissions() {
            return this.user.permissions.includes('updateWOProcesses')
        },
        packingListsFiltered() {
            let filtered = this.packingLists

            // Filter by delivery date
            if (this.deliveryDateFilterDebounced) {
                filtered = filtered.filter(pl => {
                    const deliveryDate = this.getDeliveryDate(pl)
                    return (
                        deliveryDate &&
                        deliveryDate
                            .toLowerCase()
                            .includes(
                                this.deliveryDateFilterDebounced.toLowerCase()
                            )
                    )
                })
            }

            // Filter by packing list code
            if (this.packingListFilterDebounced) {
                filtered = filtered.filter(pl => {
                    const code = pl.code || ''
                    return code
                        .toLowerCase()
                        .includes(this.packingListFilterDebounced.toLowerCase())
                })
            }

            // Filter by status (matches display label and raw value)
            if (this.statusFilterDebounced) {
                const q = this.statusFilterDebounced.toLowerCase()
                filtered = filtered.filter(pl => {
                    const display = this.getPackingListStatusText(pl)
                    return (
                        display.toLowerCase().includes(q) ||
                        (pl.status || '').toLowerCase().includes(q)
                    )
                })
            }

            // Filter by work orders
            if (this.workOrdersFilterDebounced) {
                filtered = filtered.filter(pl => {
                    if (
                        !pl.selectedWorkOrders ||
                        pl.selectedWorkOrders.length === 0
                    )
                        return false
                    const workOrdersStr = pl.selectedWorkOrders
                        .map(wo => wo?.code)
                        .join(', ')
                    return workOrdersStr
                        .toLowerCase()
                        .includes(this.workOrdersFilterDebounced.toLowerCase())
                })
            }

            // Filter by clasification
            if (this.clasificationFilterDebounced) {
                filtered = filtered.filter(pl => {
                    const clasification = (pl.clasifications ?? '').toString()
                    return clasification
                        .toLowerCase()
                        .includes(
                            this.clasificationFilterDebounced.toLowerCase()
                        )
                })
            }

            // Filter by description/notes
            if (this.descriptionFilterDebounced) {
                filtered = filtered.filter(pl => {
                    const notes = pl.notes || ''
                    return notes
                        .toLowerCase()
                        .includes(this.descriptionFilterDebounced.toLowerCase())
                })
            }

            return filtered
        },
    },
    data() {
        return {
            loading: false,
            headers: [
                {
                    text: 'DELIVERY DATE',
                    value: 'deliveryDate',
                    sortable: true,
                    width: '160px',
                    align: 'start',
                    class: 'delayed-pl-header-delivery-date',
                },
                {
                    text: 'PACKING LIST',
                    value: 'code',
                    sortable: false,
                    width: '120px',
                    align: 'start',
                },
                {
                    text: 'STATUS',
                    value: 'status',
                    sortable: false,
                    width: '120px',
                    align: 'start',
                },
                {
                    text: 'WORK ORDERS',
                    value: 'workOrders',
                    sortable: false,
                    width: '120px',
                    align: 'start',
                },
                {
                    text: 'CLASIFICATIONS',
                    value: 'clasifications',
                    sortable: false,
                    width: '120px',
                    align: 'start',
                },
                {
                    text: 'NOTES',
                    value: 'notes',
                    sortable: false,
                    width: '120px',
                    align: 'start',
                },
                {
                    text: 'ACTIONS',
                    value: 'actions',
                    sortable: false,
                    align: 'center',
                },
            ],
            packingLists: [],
            selectedPackingListForEdit: null,
            editScheduledForDialog: false,
            deliveryDateFilter: null,
            deliveryDateFilterDebounced: null,
            packingListFilter: null,
            packingListFilterDebounced: null,
            statusFilter: null,
            statusFilterDebounced: null,
            workOrdersFilter: null,
            workOrdersFilterDebounced: null,
            clasificationFilter: null,
            clasificationFilterDebounced: null,
            descriptionFilter: null,
            descriptionFilterDebounced: null,
        }
    },
    watch: {
        deliveryDateFilter: function(newValue) {
            this.debouncedDeliveryDateFilter(newValue)
        },
        packingListFilter: function(newValue) {
            this.debouncedPackingListFilter(newValue)
        },
        statusFilter: function(newValue) {
            this.debouncedStatusFilter(newValue)
        },
        workOrdersFilter: function(newValue) {
            this.debouncedWorkOrdersFilter(newValue)
        },
        clasificationFilter: function(newValue) {
            this.debouncedClasificationFilter(newValue)
        },
        descriptionFilter: function(newValue) {
            this.debouncedDescriptionFilter(newValue)
        },
        delayedPackingLists: {
            handler() {
                this.fetchDelayedPackingLists()
            },
            deep: true,
            immediate: true,
        },
        woClasificationsData: {
            handler() {
                this.fetchDelayedPackingLists()
            },
            deep: true,
        },
    },
    created() {
        this.debouncedDeliveryDateFilter = _.debounce(value => {
            this.deliveryDateFilterDebounced = value
        }, 400)
        this.debouncedPackingListFilter = _.debounce(value => {
            this.packingListFilterDebounced = value
        }, 400)
        this.debouncedStatusFilter = _.debounce(value => {
            this.statusFilterDebounced = value
        }, 400)
        this.debouncedWorkOrdersFilter = _.debounce(value => {
            this.workOrdersFilterDebounced = value
        }, 400)
        this.debouncedClasificationFilter = _.debounce(value => {
            this.clasificationFilterDebounced = value
        }, 400)
        this.debouncedDescriptionFilter = _.debounce(value => {
            this.descriptionFilterDebounced = value
        }, 400)
    },
    methods: {
        ...mapMutations(['setErrorItems']),
        async fetchDelayedPackingLists() {
            try {
                this.loading = true
                const packingLists = this.delayedPackingLists || []

                // Sort by delivery date (oldest to newest) by default
                const sortedPackingLists = (packingLists || [])
                    .map(pl => {
                        const selectedWorkOrders = pl.selectedWorkOrders || []
                        const clasifications = selectedWorkOrders
                            .map(wo => this.woClasificationsData[wo?.code])
                            .filter(
                                clasification =>
                                    clasification && clasification !== 'null'
                            )
                        return {
                            ...pl,
                            clasifications,
                        }
                    })
                    .sort((a, b) => {
                        const dateA = this.getDeliveryDateForSort(a)
                        const dateB = this.getDeliveryDateForSort(b)
                        if (!dateA && !dateB) return 0
                        if (!dateA) return 1
                        if (!dateB) return -1
                        return moment(dateA).diff(moment(dateB))
                    })

                this.packingLists = sortedPackingLists
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
        getPackingListStatusText(pl) {
            const s = pl.status == null ? '' : String(pl.status)
            return s === 'referred' ? 'inDelivery' : s
        },
        getDeliveryDate(item) {
            // Return scheduledFor or requestedShipmentDate, whichever is available
            if (item.scheduledFor) {
                return typeof item.scheduledFor === 'string'
                    ? item.scheduledFor
                    : moment(item.scheduledFor).format('YYYY-MM-DD')
            }
            if (item.requestedShipmentDate) {
                return typeof item.requestedShipmentDate === 'string'
                    ? item.requestedShipmentDate
                    : moment(item.requestedShipmentDate).format('YYYY-MM-DD')
            }
            return null
        },
        getDeliveryDateForSort(item) {
            // Return date for sorting purposes
            if (item.scheduledFor) {
                return typeof item.scheduledFor === 'string'
                    ? item.scheduledFor
                    : moment(item.scheduledFor).format('YYYY-MM-DD')
            }
            if (item.requestedShipmentDate) {
                return typeof item.requestedShipmentDate === 'string'
                    ? item.requestedShipmentDate
                    : moment(item.requestedShipmentDate).format('YYYY-MM-DD')
            }
            return null
        },
        customSort(items, index, isDesc) {
            if (index[0] === 'deliveryDate') {
                items.sort((a, b) => {
                    const dateA = this.getDeliveryDateForSort(a)
                    const dateB = this.getDeliveryDateForSort(b)
                    if (!dateA && !dateB) return 0
                    if (!dateA) return 1
                    if (!dateB) return -1
                    const diff = moment(dateA).diff(moment(dateB))
                    return isDesc[0] ? -diff : diff
                })
                return items
            }
            // Default Vuetify sorting for other columns
            return items
        },
        openUpdateScheduledFor(packingList) {
            this.selectedPackingListForEdit = packingList
            this.editScheduledForDialog = true
        },
        async openViewPackingList(item) {
            try {
                this.loading = true
                await this.$emit('openPackingList', item)
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
        handleScheduledForUpdated({ packingListId, scheduledFor }) {
            // Update local state
            const packingListIndex = this.packingLists.findIndex(
                pl => pl.id === packingListId
            )
            if (packingListIndex !== -1) {
                this.packingLists[packingListIndex].scheduledFor = scheduledFor
            }
            this.editScheduledForDialog = false
            this.selectedPackingListForEdit = null
            // Emit to parent for calendar refresh
            this.$emit('handleUpdateScheduledFor', {
                packingListId,
                scheduledFor,
            })
        },
    },
}
</script>

<style scoped></style>
